"use client";

import React, { useRef, useState, useEffect } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Avatar,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const UserInfo = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  phone,
  setPhone,
  password,
  setPassword,
  profilePicture,
  setProfilePicture,
  isEdit = false,
  onValidationChange
}) => {
  const fileRef = useRef(null);
  const [logoName, setLogoName] = useState("");
  const [logo, setLogo] = useState(null);
  const [imageError, setImageError] = useState(false);

  // ✅ Validation states
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: ""
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    password: false
  });

    useEffect(() => {
    let objectUrl;
    setImageError(false); // Reset error state

    if (!profilePicture) {
      setLogo(null);
      setLogoName("");
      return;
    }
    if (typeof profilePicture === "string") {
      // prefilled URL from backend (avatar endpoint)
      setLogo(profilePicture);
      // Optional: show the tail of the URL as "Selected"
      try {
        const last = profilePicture.split("/").pop();
        if (last) setLogoName(last);
      } catch {
        setLogoName("");
      }
    } else if (profilePicture instanceof File) {
      objectUrl = URL.createObjectURL(profilePicture);
      setLogo(objectUrl);
      setLogoName(profilePicture.name || "selected-image");
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [profilePicture]);

  // ✅ Validation functions (industry standard, not too strict)
  const validateFirstName = (value) => {
    if (!value.trim()) {
      return "First name is required";
    }
    if (value.trim().length < 2) {
      return "First name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "First name must be less than 50 characters";
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) {
      return "First name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return "";
  };

  const validateLastName = (value) => {
    if (!value.trim()) {
      return "Last name is required";
    }
    if (value.trim().length < 2) {
      return "Last name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "Last name must be less than 50 characters";
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) {
      return "Last name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) {
      return "Email is required";
    }
    // Basic email regex - not too strict
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return "Please enter a valid email address";
    }
    if (value.trim().length > 100) {
      return "Email must be less than 100 characters";
    }
    return "";
  };

  const validatePhone = (value) => {
    if (!value.trim()) {
      return "Phone number is required";
    }
    // Remove spaces, dashes, parentheses for validation
    const cleaned = value.replace(/[\s\-()]/g, "");

    // US phone number: exactly 10 digits (no country code) or 11 digits with leading 1
    if (!/^1?[0-9]{10}$/.test(cleaned)) {
      return "Please enter a valid US phone number (10 digits)";
    }
    return "";
  };

  const validatePassword = (value) => {
    if (!value) {
      return ""; // Password validation handled in parent (required only on create)
    }
    if (value.length < 6) {
      return "Password must be at least 6 characters";
    }
    if (value.length > 50) {
      return "Password must be less than 50 characters";
    }
    return "";
  };

  // ✅ Check if all fields are valid
  const isFormValid = () => {
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const emailError = validateEmail(email);
    const phoneError = validatePhone(phone);
    const passwordError = validatePassword(password);

    // In edit mode, password is optional
    const passwordValid = isEdit ? (password ? !passwordError : true) : (!passwordError && password);

    return !firstNameError && !lastNameError && !emailError && !phoneError && passwordValid;
  };

  // ✅ Notify parent component when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid());
    }
  }, [firstName, lastName, email, phone, password, isEdit]);

  // ✅ Handle field blur (show errors only after user leaves field)
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });

    let error = "";
    switch (field) {
      case "firstName":
        error = validateFirstName(firstName);
        break;
      case "lastName":
        error = validateLastName(lastName);
        break;
      case "email":
        error = validateEmail(email);
        break;
      case "phone":
        error = validatePhone(phone);
        break;
      case "password":
        error = validatePassword(password);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [field]: error });
  };

  const openPicker = () => {
    console.log("Opening file picker...");
    fileRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file (JPEG, PNG, GIF, etc.)");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("File size too large. Please select an image smaller than 5MB.");
        return;
      }

      setLogoName(file.name);
      setLogo(URL.createObjectURL(file));
      setProfilePicture(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];

    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file (JPEG, PNG, GIF, etc.)");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("File size too large. Please select an image smaller than 5MB.");
        return;
      }

      setLogoName(file.name);
      setLogo(URL.createObjectURL(file));
      setProfilePicture(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <Box component="form">
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
        }}
        className="rmui-card"
      >
        {/* Header */}
        <Box sx={{ mb: "25px" }}>
          <Typography
            variant="h3"
            sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700 }}
            className="text-black"
          >
            User Information
          </Typography>
        </Box>

        <Grid container spacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2, lg: 3 }}>
          {/* Left: Logo upload */}
          <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
            <input
              type="file"
              ref={fileRef}
              onChange={onFileChange}
              accept="image/*"
              style={{ display: "none" }}
            />

            <Box
              onClick={openPicker}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 220,
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "#fafafa",
                },
              }}
            >
              {logo && !imageError ? (
                <Avatar
                  src={logo}
                  alt="Profile Picture"
                  sx={{
                    width: 100,
                    height: 100,
                    mb: 2,
                  }}
                  slotProps={{
                    img: {
                      onError: () => {
                        console.error("Failed to load profile picture:", logo);
                        setImageError(true);
                      }
                    }
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    mb: 2,
                    bgcolor: "#f0f0f0",
                  }}
                >
                  <PersonIcon sx={{ fontSize: 50, color: "primary.main" }} />
                </Avatar>
              )}

              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                Click or drag to upload logo
              </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: "none", borderRadius: "7px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
              >
                Upload
              </Button>

              {logoName && (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
                  className="text-black"
                >
                  Selected: {logoName}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Right: Input fields */}
          <Grid size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}>
            {/* First Name */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }}
                  className="text-black"
                >
                  First Name <span style={{ color: "red" }}>*</span>
                </Typography>

                <TextField
                  label="Enter First Name"
                  variant="filled"
                  required
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    // Clear error on change if field was touched
                    if (touched.firstName) {
                      setErrors({ ...errors, firstName: validateFirstName(e.target.value) });
                    }
                  }}
                  onBlur={() => handleBlur("firstName")}
                  error={touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: `1px solid ${touched.firstName && errors.firstName ? "#d32f2f" : "#D5D9E2"}`,
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Last Name */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }}
                  className="text-black"
                >
                  Last Name <span style={{ color: "red" }}>*</span>
                </Typography>

                <TextField
                  label="Enter Last Name"
                  variant="filled"
                  required
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (touched.lastName) {
                      setErrors({ ...errors, lastName: validateLastName(e.target.value) });
                    }
                  }}
                  onBlur={() => handleBlur("lastName")}
                  error={touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: `1px solid ${touched.lastName && errors.lastName ? "#d32f2f" : "#D5D9E2"}`,
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* User Email */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }}
                  className="text-black"
                >
                  User Email ID <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  label="Enter User Email ID"
                  variant="filled"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (touched.email) {
                      setErrors({ ...errors, email: validateEmail(e.target.value) });
                    }
                  }}
                  onBlur={() => handleBlur("email")}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: `1px solid ${touched.email && errors.email ? "#d32f2f" : "#D5D9E2"}`,
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Password */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }}
                  className="text-black"
                >
                  Password <span style={{ color: "red" }}>*</span>
                </Typography>

                <TextField
                  label="Enter Password"
                  variant="filled"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (touched.password) {
                      setErrors({ ...errors, password: validatePassword(e.target.value) });
                    }
                  }}
                  onBlur={() => handleBlur("password")}
                  error={touched.password && !!errors.password}
                  helperText={touched.password ? errors.password || "Minimum 6 characters" : "Minimum 6 characters"}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: `1px solid ${touched.password && errors.password ? "#d32f2f" : "#D5D9E2"}`,
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>

            {/* Phone */}
            <Box>
              <FormControl fullWidth>
                <Typography
                  component="label"
                  sx={{ fontWeight: 500, fontSize: "14px", mb: "10px", display: "block" }}
                  className="text-black"
                >
                  User Phone Number <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  label="Enter Phone Number"
                  variant="filled"
                  required
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (touched.phone) {
                      setErrors({ ...errors, phone: validatePhone(e.target.value) });
                    }
                  }}
                  onBlur={() => handleBlur("phone")}
                  error={touched.phone && !!errors.phone}
                  helperText={touched.phone ? errors.phone || "Format: 1234567890 or (123) 456-7890" : "Format: 1234567890 or (123) 456-7890"}
                  sx={{
                    "& .MuiInputBase-root": {
                      border: `1px solid ${touched.phone && errors.phone ? "#d32f2f" : "#D5D9E2"}`,
                      backgroundColor: "#fff",
                      borderRadius: "7px",
                    },
                    "& .MuiInputBase-root::before": { border: "none" },
                    "& .MuiInputBase-root:hover::before": { border: "none" },
                  }}
                />
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default UserInfo;
